import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
  ref,
  StorageError,
  uploadBytesResumable,
  UploadTask,
} from '@firebase/storage';
import { storage } from 'lib/firebase';
import { generateId, reportError } from 'lib/util';
import FileUploadDialog from 'components/file/file-upload-dialog';
import { User } from 'lib/model';
import {
  FileUploadsDropdown,
  FileUploadsDropdownHandle,
} from 'components/file/file-uploads-dropdown';
import { useRouter } from 'next/router';
import { useWarningOnExit } from 'lib/useWarningOnExit';
import { friendlyErrorMessage } from 'components/friendly-error';
import MessageDialog, { DialogConfig } from 'components/message-dialog';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

export interface FileToUpload {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  uploadPercentage: number;
}

export default function FileUploadButton({
  user,
}: {
  user?: User;
}): ReactElement {
  const [messageDialogConfig, setMessageDialogConfig] =
    useState<DialogConfig>();
  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);
  const uploadTaskRef = useRef<UploadTask>();

  useEffect(() => {
    function updateFile(id: string, fileUpdate: Partial<FileToUpload>) {
      setFilesToUpload((filesToUpload) => {
        return filesToUpload.map((f) =>
          f.id === id ? { ...f, ...fileUpdate } : f
        );
      });
    }

    if (uploadTaskRef.current?.snapshot.state === 'running') return;
    const file = filesToUpload.find((f) => f.status === 'pending');
    if (!file) return;
    console.log('found pending upload, starting');
    updateFile(file.id, { status: 'uploading' });

    uploadTaskRef.current = createUploadTask(
      file,
      user,
      (percent) => {
        updateFile(file.id, { uploadPercentage: percent });
      },
      () => {
        updateFile(file.id, { status: 'complete' });

        // .setDoc() in case convertFile creates it first
        setDoc(
          doc(user.ref, 'files', file.id),
          {
            created: serverTimestamp(),
            uploading: true,
            transcriptStatus: 'not_requested',
            uploadedVia: 'site',
            name: file.file.name,
            deleted: null,
          },
          { merge: true }
        );
      },
      (error) => {
        if (error.code === 'storage/canceled') {
          updateFile(file.id, { status: 'complete' });
          return;
        }

        reportError(error);

        if (error.code === 'storage/unknown') {
          reportError(error.serverResponse);
        }

        updateFile(file.id, { status: 'error' });

        setMessageDialogConfig({
          title: 'Could not upload file',
          message: friendlyErrorMessage,
        });
      }
    );
  }, [user, filesToUpload]);

  useWarningOnExit(
    filesToUpload.some(
      (f) => f.status === 'pending' || f.status === 'uploading'
    ),
    'You have files still uploading, are you sure you want to leave?'
  );

  const router = useRouter();

  useEffect(() => {
    function cancelTransfers() {
      setFilesToUpload([]);
      uploadTaskRef.current?.cancel();
    }

    router.events.on('routeChangeStart', cancelTransfers);
    return () => router.events.off('routeChangeStart', cancelTransfers);
  });

  const fileUploadsDropdown = useRef<FileUploadsDropdownHandle>();

  return (
    <>
      <div className="inline-flex gap-5">
        <FileUploadsDropdown
          filesToUpload={filesToUpload}
          setUploadDialogOpen={setUploadFileDialogOpen}
          ref={fileUploadsDropdown}
          removeUpload={(file: FileToUpload) => {
            if (file.status === 'uploading') {
              console.log('cancelling upload');
              uploadTaskRef.current?.cancel();
            }

            setFilesToUpload(filesToUpload.filter((f) => f !== file));
          }}
        />
      </div>
      <FileUploadDialog
        open={uploadFileDialogOpen}
        setOpen={setUploadFileDialogOpen}
        user={user}
        onUpload={(selectedFiles: File[]) => {
          const uploadingFiles: FileToUpload[] = selectedFiles.map((file) => ({
            id: generateId(),
            file,
            status: 'pending',
            uploadPercentage: 0,
          }));

          setFilesToUpload([
            ...filesToUpload.filter((f) => f.status !== 'error'),
            ...uploadingFiles,
          ]);

          setUploadFileDialogOpen(false);
          fileUploadsDropdown.current.open();
        }}
      />
      <MessageDialog
        config={messageDialogConfig}
        setConfig={setMessageDialogConfig}
      />
    </>
  );
}

function createUploadTask(
  fileToUpload: FileToUpload,
  user: User,
  onProgress: (percent: number) => void,
  onComplete: () => void,
  onError: (error: StorageError) => void
) {
  const uploadTask = uploadBytesResumable(
    ref(
      storage,
      `users/${user.id}/${fileToUpload.file.name}#${fileToUpload.id}`
    ),
    fileToUpload.file,
    { contentType: fileToUpload.file.type }
  );

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      onProgress(
        Math.floor((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
      );
    },
    (error) => onError(error),
    () => onComplete()
  );

  return uploadTask;
}
